import { RTCSession } from '@wui/jssip/lib/RTCSession'
import { CallOptions } from '@wui/jssip/lib/UA'

import {
  CallWaitOptions,
  DEFAULT_CALL_WAIT_OPTIONS,
  NO_ANSWER_TIMEOUT_MS,
} from './call-wait-options'
import { getUA } from './use-web-rtc'

export {
  DEFAULT_CALL_WAIT_OPTIONS,
  NO_ANSWER_TIMEOUT_MS,
} from './call-wait-options'
export type { CallWaitOptions } from './call-wait-options'

export const call = (target: string, options: CallOptions): string | undefined => {
  const rtcSession = getUA()?.call(target, options)

  return rtcSession?.id
}

export const callAsync = (
  target: string,
  options: CallOptions,
  waitOptions: CallWaitOptions = DEFAULT_CALL_WAIT_OPTIONS,
): Promise<RTCSession> => {
  const ua = getUA()
  if (!ua) {
    return Promise.reject(new Error('UA is not initialized'))
  }

  return ua.callAsync(target, options, {
    resolveOn: waitOptions.resolveOn ?? DEFAULT_CALL_WAIT_OPTIONS.resolveOn,
    timeoutMs: waitOptions.timeoutMs ?? DEFAULT_CALL_WAIT_OPTIONS.timeoutMs,
  })
}

export const waitForIncomingSession = (
  timeoutMs = NO_ANSWER_TIMEOUT_MS,
): Promise<RTCSession> => {
  const ua = getUA()
  if (!ua) {
    return Promise.reject(new Error('UA is not initialized'))
  }

  return ua.waitForIncomingSession(timeoutMs)
}

export const waitForSessionEstablished = (
  session: RTCSession,
  waitOptions: CallWaitOptions = DEFAULT_CALL_WAIT_OPTIONS,
): Promise<RTCSession> => {
  if (session.isEstablished()) {
    return Promise.resolve(session)
  }

  const resolveOn = waitOptions.resolveOn ?? DEFAULT_CALL_WAIT_OPTIONS.resolveOn
  const timeoutMs = waitOptions.timeoutMs ?? DEFAULT_CALL_WAIT_OPTIONS.timeoutMs

  return new Promise((resolve, reject) => {
    let settled = false
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const cleanup = () => {
      session.removeListener('accepted', onAccepted)
      session.removeListener('confirmed', onConfirmed)
      session.removeListener('failed', onFailed)
      session.removeListener('ended', onEnded)
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
    }

    const finishResolve = () => {
      if (settled) return
      settled = true
      cleanup()
      resolve(session)
    }

    const finishReject = (error: unknown) => {
      if (settled) return
      settled = true
      cleanup()
      const cause = error && typeof error === 'object' && 'cause' in error
        ? (error as { cause?: unknown }).cause
        : error
      reject(cause ?? error)
    }

    const onAccepted = () => finishResolve()
    const onConfirmed = () => finishResolve()
    const onFailed = (data: unknown) => finishReject(data)
    const onEnded = (data: unknown) => finishReject(data)

    if (resolveOn === 'confirmed') {
      session.once('confirmed', onConfirmed)
    } else {
      session.once('accepted', onAccepted)
    }
    session.once('failed', onFailed)
    session.once('ended', onEnded)

    if (typeof timeoutMs === 'number' && timeoutMs > 0) {
      timeoutId = setTimeout(() => {
        finishReject(new Error('waitForSessionEstablished timed out'))
      }, timeoutMs)
    }
  })
}
